import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from '@angular/core';
import { Language } from "@app/core/enums/Language";
import { ValidationService } from "@app/core/services/validation.service";
import { BatchShipmentsOrder } from "@app/shared/CSDAModels/BatchShipmentsOrder";
import { GetBatchShipmentsHistoryDetail } from "@app/shared/CSDAModels/GetBatchShipmentsHistoryDetail";
import { GetBatchShipmentsReportDetail } from "@app/shared/CSDAModels/GetBatchShipmentsReportDetail";
import { GetBatchShipmentsReportResult } from "@app/shared/CSDAModels/GetBatchShipmentsReportResult";
import { GetImportTemplateDetail } from "@app/shared/CSDAModels/GetImportTemplateDetail";
import { ImportResult } from "@app/shared/CSDAModels/ImportResult";
import { environment } from "@env";
import { TranslateService } from "@ngx-translate/core";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import Swal from "sweetalert2";

@Injectable({
  providedIn: 'root'
})
export class ImportService {

  constructor(
    private httpClient: HttpClient,
		private translateService: TranslateService,
    private validationService: ValidationService,
  ) { }

  //------------------------
  // Get the template for importing orders
  //------------------------
  public getTemplate(): any {

    const Detail: GetImportTemplateDetail = {
      Language: this.translateService.currentLang === "fr" ? Language.French : Language.English,
      IsDevelopmentEnvironment: false
    };

    //--------------------------------------------------------------------------------------------------------------------------------------
    // Après 2 longues journées de recherche intensive pour comprendre pourquoi j'avais une erreur de Observable.  Voici le lien qui m'a aidé
    // https://www.concretepage.com/angular/angular-httpclient-post#:~:text=%7D%0A%20)%3B%0A%7D-,HttpClient,-.post%20and%20Response
    // J'ai donc ajouté , { observe: 'body', responseType: 'blob'}
    // L'erreur était : You provided 'undefined' where a stream was expected. You can provide an Observable, Promise, Array, or Iterable.
    //--------------------------------------------------------------------------------------------------------------------------------------
    return this.httpClient.post(environment.CSDAEndPoint + "/CSDA/GetImportTemplate", Detail, { observe: 'body', responseType: 'blob'})
        .pipe(
            map(data => {
                var validation = this.validationService.validate(data);

                if (validation.isValid === false) {
                    Swal.fire('', validation.message, "error");
                    return null;
                }

                if (validation.message !== "") {
                    Swal.fire('', validation.message, "info");
                }

                return data;
            })          
        );
  }

  //----------------------------
  // Get Batch Shipments History
  //----------------------------
  public getBatchShipmentsHistory(): any {

    const Detail: GetBatchShipmentsHistoryDetail = {
      Language: this.translateService.currentLang === "fr" ? Language.French : Language.English,
      IsDevelopmentEnvironment: false
    };

    return this.httpClient.post(environment.CSDAEndPoint + "/CSDA/GetBatchShipmentsHistory", Detail, { observe: 'body', responseType: 'json' })
      .pipe(
        map(data => {
          const validation = this.validationService.validate(data);

          if (validation.isValid === false) {
            Swal.fire('', validation.message, "error");
            return null;
          }

          if (validation.message !== "") {
            Swal.fire('', validation.message, "info");
          }

          return data;
        }),
        catchError(error => {
          return of([]);
        })
      );
  }

  //----------------------------
  // Get Batch Shipments Report
  //----------------------------
  public getBatchShipmentsReport(ImportOrdersHeaderId: number): Observable<GetBatchShipmentsReportResult | null> {
    const Detail: GetBatchShipmentsReportDetail = {
      Language: this.translateService.currentLang === "fr" ? Language.French : Language.English,
      IsDevelopmentEnvironment: false,
      ImportOrdersHeaderId: ImportOrdersHeaderId
    };

    return this.httpClient.post<GetBatchShipmentsReportResult>(environment.CSDAEndPoint + "/CSDA/GetBatchShipmentsReport",Detail, { observe: 'body' as const, responseType: 'json' as const })
    .pipe(
      map((data: GetBatchShipmentsReportResult) => {
        const validation = this.validationService.validate(data);

        if (!validation.isValid) {
          Swal.fire('', validation.message, "error");
          return null;
        }

        if (validation.message !== "") {
          Swal.fire('', validation.message, "info");
        }

        return data;
      }),
      catchError(error => {
        Swal.fire('', 'Erreur lors de la récupération du rapport', 'error');
        return of(null);
      })
    );
  }
  public getBatchWaybills(importOrderHeaderId: number): any {

    const Detail = {
      ImportOrdersHeaderId: importOrderHeaderId,
      Language: this.translateService.currentLang === "fr" ? Language.French : Language.English,
      IsDevelopmentEnvironment: false
    };

    return this.httpClient.post(
      environment.CSDAEndPoint + "/CSDA/GetBatchWaybills",
      Detail,
      { observe: 'body', responseType: 'blob' }
    ).pipe(
      map((data: Blob) => data), // just pass the Blob to the component
      catchError(error => {
        Swal.fire('', 'Error while generating waybills PDF', 'error');
        return of(null);
      })
    );
  }
  reprocessShipments(importOrderHeaderId: number): Observable<ImportResult | null> {
    const Detail = {
      Language: this.translateService.currentLang === "fr" ? Language.French : Language.English,
      IsDevelopmentEnvironment: false,
      ImportOrdersHeaderId: importOrderHeaderId
    };

    return this.httpClient.post<ImportResult>(environment.CSDAEndPoint + "/CSDA/ReprocessBatchShipments", Detail)
    .pipe(
      map((data: ImportResult) => {
        const validation = this.validationService.validate(data);

        if (!validation.isValid) {
          Swal.fire('', validation.message, "error");
          return null;
        }

        if (validation.message !== "") {
          Swal.fire('', validation.message, "info");
          return null;
        }

        return data;
      }),
      catchError(error => {
        return of(null);
      })
    );
  }
}
